/**
 * Unified service registry — all ports / compose services / dev metadata in one place.
 *
 * 参照関係:
 *  - 実行時 (widgets, api-client): `talknClients` が clientKey を持つエントリだけを公開（既存インターフェース）
 *  - ch-admin /dev ページ: SERVICES 全体をそのまま使用して起動/停止/状態表示
 *  - common/scripts/get-port.ts: `talknClients` 経由でポート取得
 *
 * 新しいサービス・ポート変更はこのファイル一箇所のみで完結する。
 */
import {
  tuneSubscribe,
  tuneSubscribeOptionInit,
} from "../subscription/TuneSubscribeOption";
import type {
  TuneProjection,
  TuneProjectionKey,
} from "../subscription/TuneProjection";

const withTuneOptions = (enabledKeys: TuneProjectionKey[]): TuneProjection => ({
  ...tuneSubscribeOptionInit,
  ...Object.fromEntries(enabledKeys.map((key) => [key, true])),
});

const defaultTuneSubscribeKeys: TuneProjectionKey[] = [
  tuneSubscribe.stateTuneChLatestPost,
  tuneSubscribe.stateTuneChMeta,
  tuneSubscribe.statePosts,
  tuneSubscribe.stateRank,
  tuneSubscribe.stateRankLatestPost,
  tuneSubscribe.stateRankMeta,
  tuneSubscribe.stateRankMetaFavicon,
  tuneSubscribe.stateRankAll,
  tuneSubscribe.stateRankAllLatestPost,
  tuneSubscribe.stateRankAllMeta,
  tuneSubscribe.stateRankAllMetaFavicon,
];

const createDefaultTuneOption = (): TuneProjection =>
  withTuneOptions(defaultTuneSubscribeKeys);

export type ServiceKind = "node" | "docker";
export type ServiceCategory = "docker-db" | "docker-server" | "core" | "widgets" | "sites" | "others";
export type WidgetAdminCategory = "livecnt" | "ranking" | "thread" | "analytics" | "notification";

export type WidgetMetadata = {
  appId: string;
  name: string;
  description: string;
  path: string;
  iconKey: string;
  iconBg: string;
  defaultClassName: string;
  adminCategory: WidgetAdminCategory;
};

export type ServiceVariant = {
  port: number;
  tuneOption?: TuneProjection;
};

export type Service = {
  /** DEV ページ等で使う安定 ID */
  id: string;
  label: string;
  category: ServiceCategory;
  kind: ServiceKind;
  port: number;
  /** dev-dashboard の「開く」で使う URL。未指定ならボタン非表示 */
  openUrl?: string;

  /** talknClients[] のキー（<div class="talkn-*">, api-client 等から参照） */
  clientKey?: string;
  tuneOption?: TuneProjection;
  /** portal.variants.multi のような追加ポート */
  variants?: Record<string, ServiceVariant>;

  /** node サービス: 開発サーバー制御用メタデータ */
  cwd?: string;
  command?: string;
  args?: readonly string[];
  controllable?: boolean;

  /** docker サービス */
  composeCwd?: string;
  composeFile?: string;
  composeService?: string;

  /** dev-dashboard で非表示にする */
  hidden?: boolean;

  /** 対応する本番デプロイターゲット ID (devTopology.ts の InfraTarget.id) */
  prodTarget?: string;

  /** ch-admin / dev-dashboard で使う widget 管理メタデータ */
  widget?: WidgetMetadata;
};

export const SERVICES: readonly Service[] = [
  // ---------- Docker (DB) ----------
  {
    id: "docker:postgres",
    label: "Postgres",
    category: "docker-db",
    kind: "docker",
    port: 5432,
    composeCwd: "ch-server",
    composeService: "postgres",
    controllable: true,
    prodTarget: "live",
  },
  {
    id: "docker:redis-root",
    label: "Redis (root)",
    category: "docker-db",
    kind: "docker",
    port: 6379,
    composeCwd: "ch-server",
    composeService: "redis-root",
    controllable: true,
    prodTarget: "live",
  },

  // ---------- Docker (Server) ----------
  {
    id: "docker:ch-gateway-root",
    label: "ch-gateway",
    category: "docker-server",
    kind: "docker",
    port: 10444,
    openUrl: "https://localhost:10444",
    composeCwd: "common/build/ch-configs.development",
    composeFile: "compose.gateway.yml",
    composeService: "root",
    controllable: true,
    prodTarget: "live",
  },
  {
    id: "docker:io-root",
    label: "ch-server",
    category: "docker-server",
    kind: "docker",
    port: 10444,
    composeCwd: "ch-server",
    composeService: "io-root",
    controllable: true,
    prodTarget: "live",
  },

  // ---------- Core ----------
  {
    id: "dev:dashboard",
    label: "dev-dashboard",
    category: "core",
    kind: "node",
    port: 4000,
    cwd: "dev-dashboard",
    command: "npm",
    args: ["run", "dev"],
    hidden: true,
  },
  {
    id: "core:control-plane",
    clientKey: "control-plane-frontend",
    label: "control-plane",
    category: "core",
    kind: "node",
    port: 9003,
    openUrl: "http://localhost:9003",
    cwd: "control-plane",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "control",
  },
  {
    id: "core:ch-admin",
    clientKey: "ch-admin",
    label: "ch-admin",
    category: "core",
    kind: "node",
    port: 9002,
    openUrl: "https://localhost:9002",
    tuneOption: createDefaultTuneOption(),
    cwd: "ch-admin",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "admin",
  },
  {
    id: "core:api-client",
    clientKey: "api-client",
    label: "api-client",
    category: "core",
    kind: "node",
    port: 8000,
    openUrl: "https://localhost:8000",
    tuneOption: createDefaultTuneOption(),
    cwd: "api-client",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-api-client",
  },

  // ---------- Sites ----------
  {
    id: "sites:www",
    label: "sites/www",
    category: "sites",
    kind: "node",
    port: 3001,
    openUrl: "http://localhost:3001",
    cwd: "sites/www",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "vercel-www",
  },
  {
    id: "sites:news",
    label: "sites/news",
    category: "sites",
    kind: "node",
    port: 3002,
    openUrl: "http://localhost:3002",
    cwd: "sites/news",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "vercel-news",
  },
  {
    id: "others:sales",
    label: "sales",
    category: "others",
    kind: "node",
    port: 3010,
    openUrl: "http://localhost:3010",
    cwd: "market/sales",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
  },
  {
    id: "others:x-manager",
    label: "x-manager",
    category: "others",
    kind: "node",
    port: 3011,
    openUrl: "http://localhost:3011",
    cwd: "market/x",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
  },

  // ---------- Widgets ----------
  {
    id: "widget:card",
    clientKey: "card",
    label: "widgets/ranking/horizonCard",
    category: "widgets",
    kind: "node",
    port: 8082,
    openUrl: "https://localhost:8082",
    tuneOption: createDefaultTuneOption(),
    cwd: "widgets/ranking/horizonCard",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-widgets",
    widget: {
      appId: "horizonCard",
      name: "HorizonCard",
      description: "カード表示アプリケーション - スタンドアロンカードビューコンポーネント",
      path: "widgets/ranking/horizonCard",
      iconKey: "horizonCard",
      iconBg: "#8b5cf6",
      defaultClassName: "talkn-card",
      adminCategory: "ranking",
    },
  },
  {
    id: "sites:portal",
    clientKey: "portal",
    label: "sites/portal",
    category: "sites",
    kind: "node",
    port: 8090,
    openUrl: "https://localhost:8090",
    tuneOption: createDefaultTuneOption(),
    variants: {
      multi: { port: 8091, tuneOption: createDefaultTuneOption() },
    },
    cwd: "sites/portal",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "vercel-portal",
  },
  {
    id: "widget:notification",
    clientKey: "talkn-notification",
    label: "widgets/notification",
    category: "widgets",
    kind: "node",
    port: 8084,
    openUrl: "https://localhost:8084",
    tuneOption: withTuneOptions([
      tuneSubscribe.stateRankAll,
      tuneSubscribe.stateRankAllMeta,
      tuneSubscribe.stateRankAllMetaFavicon,
    ]),
    cwd: "widgets/notification",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-widgets",
    widget: {
      appId: "notification",
      name: "Notification",
      description: "通知管理アプリケーション - リアルタイム通知とアラート管理システム",
      path: "widgets/notification",
      iconKey: "notification",
      iconBg: "#10b981",
      defaultClassName: "talkn-notification",
      adminCategory: "notification",
    },
  },
  {
    id: "widget:ranking",
    clientKey: "talkn-ranking",
    label: "widgets/ranking/vertical-index",
    category: "widgets",
    kind: "node",
    port: 8085,
    openUrl: "https://localhost:8085",
    tuneOption: withTuneOptions([
      tuneSubscribe.stateRankAll,
      tuneSubscribe.stateRankAllLatestPost,
      tuneSubscribe.stateRankAllMeta,
      tuneSubscribe.stateRankAllMetaFavicon,
    ]),
    cwd: "widgets/ranking/vertical-index",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-widgets",
    widget: {
      appId: "vertical-index",
      name: "VerticalIndex",
      description: "ランキング表示アプリケーション - 人気チャンネルを縦リストで表示",
      path: "widgets/ranking/vertical-index",
      iconKey: "verticalIndex",
      iconBg: "#2cb5a0",
      defaultClassName: "talkn-ranking",
      adminCategory: "ranking",
    },
  },
  {
    id: "widget:badge",
    clientKey: "talkn-badge",
    label: "widgets/liveCnt/badge",
    category: "widgets",
    kind: "node",
    port: 8086,
    openUrl: "https://localhost:8086",
    tuneOption: withTuneOptions([tuneSubscribe.stateRankAll]),
    cwd: "widgets/liveCnt/badge",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-widgets",
    widget: {
      appId: "badge",
      name: "Badge",
      description: "浮遊ライブバッジ - サイト全体の同時閲覧人数を表示するソーシャルプルーフ",
      path: "widgets/liveCnt/badge",
      iconKey: "badge",
      iconBg: "#ef4444",
      defaultClassName: "talkn-badge",
      adminCategory: "livecnt",
    },
  },
  {
    id: "widget:ticker",
    clientKey: "talkn-ticker",
    label: "widgets/ranking/ticker",
    category: "widgets",
    kind: "node",
    port: 8087,
    openUrl: "https://localhost:8087",
    tuneOption: withTuneOptions([
      tuneSubscribe.stateRankAll,
      tuneSubscribe.stateRankAllMeta,
      tuneSubscribe.stateRankAllMetaFavicon,
    ]),
    cwd: "widgets/ranking/ticker",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-widgets",
    widget: {
      appId: "ticker",
      name: "Ticker",
      description: "ライブティッカー - 横スクロール式ニュースティッカー風のライブチャンネル表示",
      path: "widgets/ranking/ticker",
      iconKey: "ticker",
      iconBg: "#1a1a1a",
      defaultClassName: "talkn-ticker",
      adminCategory: "ranking",
    },
  },
  {
    id: "widget:story",
    clientKey: "talkn-story",
    label: "widgets/ranking/story",
    category: "widgets",
    kind: "node",
    port: 8088,
    openUrl: "https://localhost:8088",
    tuneOption: withTuneOptions([
      tuneSubscribe.stateRankAll,
      tuneSubscribe.stateRankAllMeta,
      tuneSubscribe.stateRankAllMetaFavicon,
    ]),
    cwd: "widgets/ranking/story",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-widgets",
    widget: {
      appId: "story",
      name: "Story",
      description: "ストーリー表示 - Instagram風の丸サムネイル横スクロール",
      path: "widgets/ranking/story",
      iconKey: "story",
      iconBg: "#ec4899",
      defaultClassName: "talkn-story",
      adminCategory: "ranking",
    },
  },
  {
    id: "widget:circle",
    clientKey: "talkn-circle",
    label: "widgets/liveCnt/circle",
    category: "widgets",
    kind: "node",
    port: 8096,
    openUrl: "https://localhost:8096",
    tuneOption: withTuneOptions([tuneSubscribe.stateRankAll]),
    cwd: "widgets/liveCnt/circle",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-widgets",
    widget: {
      appId: "circle",
      name: "Circle",
      description: "サークルバッジ - 丸型ライブ閲覧人数表示",
      path: "widgets/liveCnt/circle",
      iconKey: "circle",
      iconBg: "rgba(79, 174, 159, 1)",
      defaultClassName: "talkn-circle",
      adminCategory: "livecnt",
    },
  },
  {
    id: "widget:heatmap",
    clientKey: "talkn-heatmap",
    label: "widgets/analytics/heatmap",
    category: "widgets",
    kind: "node",
    port: 8089,
    openUrl: "https://localhost:8089",
    tuneOption: withTuneOptions([
      tuneSubscribe.stateRankAll,
      tuneSubscribe.stateRankAllMeta,
    ]),
    cwd: "widgets/analytics/heatmap",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-widgets",
    widget: {
      appId: "heatmap",
      name: "Heatmap",
      description: "リンクオーバーレイ - 導入ページの既存リンクにライブ人数バッジを自動表示",
      path: "widgets/analytics/heatmap",
      iconKey: "heatmap",
      iconBg: "#f59e0b",
      defaultClassName: "talkn-heatmap",
      adminCategory: "analytics",
    },
  },
  {
    id: "widget:charts",
    clientKey: "talkn-charts",
    label: "widgets/analytics/charts",
    category: "widgets",
    kind: "node",
    port: 8092,
    openUrl: "https://localhost:8092",
    tuneOption: withTuneOptions([
      tuneSubscribe.stateTuneChMeta,
      tuneSubscribe.stateRankAll,
    ]),
    cwd: "widgets/analytics/charts",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-widgets",
    widget: {
      appId: "charts",
      name: "Charts",
      description: "チャンネル実績チャート - ライブユーザー推移・ランキングをリアルタイム表示",
      path: "widgets/analytics/charts",
      iconKey: "charts",
      iconBg: "#6366f1",
      defaultClassName: "talkn-charts",
      adminCategory: "analytics",
    },
  },
  {
    id: "widget:thread",
    clientKey: "talkn-thread",
    label: "widgets/thread/thread",
    category: "widgets",
    kind: "node",
    port: 8093,
    openUrl: "https://localhost:8093",
    tuneOption: withTuneOptions([
      tuneSubscribe.stateTuneChMeta,
      tuneSubscribe.statePosts,
    ]),
    cwd: "widgets/thread/thread",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-widgets",
    widget: {
      appId: "thread",
      name: "Thread",
      description: "スレッド表示 - リアルタイム投稿タイムライン（表示専用）",
      path: "widgets/thread/thread",
      iconKey: "thread",
      iconBg: "#0ea5e9",
      defaultClassName: "talkn-thread",
      adminCategory: "thread",
    },
  },
  {
    id: "widget:post",
    clientKey: "talkn-post",
    label: "widgets/thread/post",
    category: "widgets",
    kind: "node",
    port: 8094,
    openUrl: "https://localhost:8094",
    tuneOption: withTuneOptions([tuneSubscribe.stateTuneChMeta]),
    cwd: "widgets/thread/post",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-widgets",
    widget: {
      appId: "post",
      name: "Post",
      description: "投稿フォーム - 単独でマウント可能なポストフォーム",
      path: "widgets/thread/post",
      iconKey: "post",
      iconBg: "#06b6d4",
      defaultClassName: "talkn-post",
      adminCategory: "thread",
    },
  },
  {
    id: "widget:emotions",
    clientKey: "talkn-emotions",
    label: "widgets/thread/emotions",
    category: "widgets",
    kind: "node",
    port: 8095,
    openUrl: "https://localhost:8095",
    tuneOption: createDefaultTuneOption(),
    cwd: "widgets/thread/emotions",
    command: "npm",
    args: ["run", "dev"],
    controllable: true,
    prodTarget: "s3-widgets",
    widget: {
      appId: "emotions",
      name: "Emotions",
      description: "感情レーダー - 投稿内容とスタンプを感情モデルで集計してグラフ表示",
      path: "widgets/thread/emotions",
      iconKey: "emotions",
      iconBg: "#14b8a6",
      defaultClassName: "talkn-emotions",
      adminCategory: "thread",
    },
  },
];

export const WIDGET_SERVICES = SERVICES.filter((service): service is Service & { widget: WidgetMetadata } =>
  service.category === "widgets" && Boolean(service.widget)
);

/**
 * 既存の talknClients 形式（client-side 専用）を SERVICES から導出。
 * clientKey を持つエントリのみ公開する。
 */
type ClientEntry = {
  port: number;
  tuneOption?: TuneProjection;
  variants?: Record<string, ServiceVariant>;
};

export const talknClients: Record<string, ClientEntry> = (() => {
  const out: Record<string, ClientEntry> = {};
  for (const s of SERVICES) {
    if (!s.clientKey) continue;
    out[s.clientKey] = {
      port: s.port,
      ...(s.tuneOption ? { tuneOption: s.tuneOption } : {}),
      ...(s.variants ? { variants: s.variants } : {}),
    };
  }
  return out;
})();
